// VastuChartVariants.js — house→direction/wall label tables for the three
// D1-chart variants (Vedic Vastu Chart, AstroVastu Chart, Laal Kitaab Makaan
// Chart). All three reuse the exact same 12 house positions and the exact
// same computed Ascendant + planet data as the plain D1 chart — only the
// LABEL shown in each house differs. No second API call, no new data source.
//
// Standard Vedic-Vastu house→direction convention (Lagna/House1 = East,
// derived from the Kalapurusha model: House1=East, House4=North, House7=West,
// House10=South are the four fixed cardinal anchors — confirmed via
// PocketPandit's House-Direction guide and AstroCulture's house-direction
// article), extended to the finer 16-point compass by stepping -30° per
// house (clockwise around the chart) from House1=90°(E):
//   1=E 2=ENE 3=NNE 4=N 5=NNW 6=WNW 7=W 8=WSW 9=SSW 10=S 11=SSE 12=ESE

export const VEDIC_VASTU_DIRECTIONS = {
  1: 'E', 2: 'ENE', 3: 'NNE', 4: 'N', 5: 'NNW', 6: 'WNW',
  7: 'W', 8: 'WSW', 9: 'SSW', 10: 'S', 11: 'SSE', 12: 'ESE',
};

// Laal Kitaab Makaan Chart — the coarser, "wall"-styled version of the same
// house-direction convention: the 4 Kendra (kite) houses become the 4
// walls, and the 8 corner houses pair up into the 4 intercardinal zones
// (House2&3=NE, House5&6=NW, House8&9=SW, House11&12=SE — matching the
// standard coarse 8-direction table), with House9 singled out as "Center"
// per Laal Kitaab's distinguishing extra "central place" direction (the
// system's own signature feature beyond standard Vastu's 8 directions).
export const LAAL_KITAAB_DIRECTIONS = {
  1: 'Eastern Wall', 2: 'North-East', 3: 'North-East', 4: 'North Wall',
  5: 'North-West',   6: 'North-West', 7: 'West Wall',
  8: 'South-West',   9: 'Center',     10: 'Southern Wall',
  11: 'South-East',  12: 'South-East',
};

// 16-point compass, in bearing order (0°=N, going clockwise)
const COMPASS_16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const norm360 = (deg) => ((deg % 360) + 360) % 360;
const bearingToLabel = (deg) => COMPASS_16[Math.round(norm360(deg) / 22.5) % 16];

/**
 * AstroVastu Chart — the person-specific version. VEDIC_VASTU_DIRECTIONS
 * above assumes the Ascendant sits exactly mid-sign (15° in), so each
 * house's centre bearing (90 - (house-1)*30) lands exactly on a 16-point
 * compass label with no ambiguity. A real chart's Ascendant almost never
 * sits at exactly 15° — it's `ascendantNormDegree` (0-30°) into its sign —
 * so this version shifts every house's centre bearing by that same amount
 * before snapping to the nearest 16-point label, exactly like the static
 * table does at its one fixed assumption. (An earlier version instead
 * sampled the two edges of each house's 30°-wide window and joined them as
 * "X & Y" when they differed — since a 30°-wide window can never fit inside
 * a single 22.5°-wide compass slice, that ALWAYS differed, so every house on
 * every chart showed a nonsensical compound label — e.g. House1 showed
 * "ENE & ESE" instead of "E" even in the exact case that should reduce to
 * the static table above. Fixed by snapping the centre bearing directly,
 * verified to reproduce VEDIC_VASTU_DIRECTIONS exactly when
 * ascendantNormDegree=15.)
 */
export const computeAstroVastuDirections = (ascendantNormDegree = 0) => {
  const offset = ascendantNormDegree - 15; // shift from the "centred at 15°" assumption
  const result = {};
  for (let house = 1; house <= 12; house++) {
    const centerBearing = 90 - (house - 1) * 30 - offset;
    result[house] = bearingToLabel(centerBearing);
  }
  return result;
};
